import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import PostAnonAccountPrompt from '../components/PostAnonAccountPrompt';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { MapPin, Filter, Loader2, Image, Video, FileText, ExternalLink, CalendarIcon, Eye, ArrowLeft, Map, Plus, Pencil, Trash2, Users, PlayCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Convert any YouTube URL (watch?v=, youtu.be/, shorts/, embed/) to embed URL
const getYouTubeEmbedUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  // Match common YouTube URL patterns and extract the video ID
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = trimmed.match(p);
    if (m && m[1]) return `https://www.youtube.com/embed/${m[1]}`;
  }
  return null;
};

const SiteCard = ({ site, onEdit, onDelete, isAgentOrAdmin }) => (
  <Card className="card-hover bg-card border-none rounded-2xl overflow-hidden h-full" data-testid={`site-card-${site.site_id}`}>
    <Link to={`/sites/${site.site_id}`}>
      <div className="relative h-48">
        {site.photos?.[0] ? (
          <img src={site.photos[0]} alt={site.nom || site.ville} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <Image className="w-12 h-12 text-muted-foreground" />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <Badge className="bg-primary text-primary-foreground">
            {site.type_document?.replace('_', ' ') || 'Non spécifié'}
          </Badge>
        </div>
        {site.archive && (
          <div className="absolute top-3 right-3">
            <Badge className="bg-gray-500 text-white">Archivé</Badge>
          </div>
        )}
      </div>
      <CardContent className="p-4">
        <h3 className="text-xl font-semibold font-serif mb-2">{site.nom || site.ville}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{site.description}</p>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="w-4 h-4 text-primary" />
          <span>{site.ville}, Sénégal</span>
        </div>
      </CardContent>
    </Link>
    <CardFooter className="p-4 pt-0 flex flex-col gap-2">
      <Link to={`/sites/${site.site_id}`} className="w-full">
        <Button variant="outline" className="w-full">
          <Eye className="w-4 h-4 mr-2" /> Voir le site
        </Button>
      </Link>
      {isAgentOrAdmin && (
        <div className="flex gap-2 w-full">
          <Button size="sm" variant="outline" className="flex-1" onClick={(e) => { e.preventDefault(); onEdit(site); }}>
            <Pencil className="w-3 h-3 mr-1" /> Modifier
          </Button>
          <Button size="sm" variant="outline" className="text-red-600" onClick={(e) => { e.preventDefault(); onDelete(site); }}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
    </CardFooter>
  </Card>
);

export function SitesList() {
  const navigate = useNavigate();
  const { token, isAgentOrAdmin } = useAuth();
  const [sites, setSites] = useState([]);
  const [villes, setVilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Edit/Create dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [formData, setFormData] = useState({
    nom: '', description: '', ville: '', latitude: '', longitude: '', type_document: '', photos: [], video_url: ''
  });
  
  const [filters, setFilters] = useState({ ville: '', type_document: '' });

  const fetchData = async () => {
    try {
      const [sitesRes, villesRes] = await Promise.all([
        fetch(`${API_URL}/api/sites`),
        fetch(`${API_URL}/api/villes`)
      ]);
      setSites(await sitesRes.json());
      setVilles(await villesRes.json());
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = () => {
    setEditingSite(null);
    setFormData({ nom: '', description: '', ville: '', latitude: '', longitude: '', type_document: '', photos: [], video_url: '' });
    setEditDialogOpen(true);
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    setFormData({
      nom: site.nom || site.ville || '',
      description: site.description || '',
      ville: site.ville || '',
      latitude: site.latitude?.toString() || '',
      longitude: site.longitude?.toString() || '',
      type_document: site.type_document || '',
      photos: site.photos || [],
      video_url: site.video_url || ''
    });
    setEditDialogOpen(true);
  };

  const handleDelete = async (site) => {
    if (!window.confirm(`Supprimer le site "${site.nom || site.ville}" ?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/sites/${site.site_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur');
      toast.success('Site supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
      const body = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        photos: formData.photos.filter(p => p.trim())
      };
      
      const url = editingSite ? `${API_URL}/api/sites/${editingSite.site_id}` : `${API_URL}/api/sites`;
      const response = await fetch(url, {
        method: editingSite ? 'PUT' : 'POST',
        headers, body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Erreur');
      toast.success(editingSite ? 'Site mis à jour' : 'Site créé');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const filteredSites = sites.filter(s => {
    if (filters.ville && s.ville !== filters.ville) return false;
    if (filters.type_document && s.type_document !== filters.type_document) return false;
    return true;
  });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-bold font-serif">Sites</h1>
              <p className="text-muted-foreground">Découvrez nos lotissements disponibles</p>
            </div>
          </div>
          {isAgentOrAdmin() && (
            <Button onClick={handleCreate} className="btn-pill">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un site
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-8 p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs mb-1 block">Ville</Label>
              <Select value={filters.ville} onValueChange={(v) => setFilters({...filters, ville: v === 'all' ? '' : v})}>
                <SelectTrigger><Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Toutes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les villes</SelectItem>
                  {villes.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs mb-1 block">Type document</Label>
              <Select value={filters.type_document} onValueChange={(v) => setFilters({...filters, type_document: v === 'all' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="deliberation_mairie">Délibération mairie</SelectItem>
                  <SelectItem value="bail">Bail</SelectItem>
                  <SelectItem value="titre_foncier">Titre foncier</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSites.map(site => (
            <SiteCard 
              key={site.site_id} 
              site={site} 
              onEdit={handleEdit}
              onDelete={handleDelete}
              isAgentOrAdmin={isAgentOrAdmin()}
            />
          ))}
        </div>

        {filteredSites.length === 0 && (
          <div className="text-center py-12">
            <MapPin className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Aucun site trouvé</p>
          </div>
        )}

        {/* Edit/Create Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSite ? 'Modifier le site' : 'Nouveau site'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nom du site *</Label>
                <Input
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  placeholder="Ex: Cité les Palmiers, Résidence Teranga..."
                  data-testid="site-nom-input"
                />
              </div>
              <div>
                <Label>Ville *</Label>
                <Select value={formData.ville} onValueChange={(v) => setFormData({...formData, ville: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>{villes.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Latitude</Label><Input type="number" step="0.0001" value={formData.latitude} onChange={(e) => setFormData({...formData, latitude: e.target.value})} /></div>
                <div><Label>Longitude</Label><Input type="number" step="0.0001" value={formData.longitude} onChange={(e) => setFormData({...formData, longitude: e.target.value})} /></div>
              </div>
              <div>
                <Label>Type de document</Label>
                <Select value={formData.type_document} onValueChange={(v) => setFormData({...formData, type_document: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deliberation_mairie">Délibération mairie</SelectItem>
                    <SelectItem value="bail">Bail</SelectItem>
                    <SelectItem value="titre_foncier">Titre foncier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Photos</Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    const newPhotos = [];
                    for (const file of files) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error(`${file.name} trop volumineux (max 5MB)`);
                        continue;
                      }
                      const dataUrl = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = (ev) => resolve(ev.target.result);
                        reader.readAsDataURL(file);
                      });
                      newPhotos.push(dataUrl);
                    }
                    setFormData({ ...formData, photos: [...formData.photos, ...newPhotos] });
                  }}
                  data-testid="site-photos-upload"
                />
                {formData.photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.photos.map((photo, idx) => (
                      <div key={idx} className="relative">
                        <img src={photo} alt={`Photo ${idx + 1}`} className="w-16 h-16 object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, photos: formData.photos.filter((_, i) => i !== idx) })}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                          data-testid={`remove-site-photo-${idx}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Vidéo YouTube du site (optionnel)</Label>
                <Input
                  type="url"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  data-testid="site-video-url-input"
                />
                <p className="text-xs text-muted-foreground">Collez le lien YouTube (watch, youtu.be, shorts ou embed).</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving || !formData.ville || !formData.nom?.trim()} data-testid="save-site-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingSite ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export function SiteDetail() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAgentOrAdmin } = useAuth();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [requesting, setRequesting] = useState(false);
  // Anon flow
  const [anonData, setAnonData] = useState({ nom: '', prenom: '', email: '', telephone: '' });
  const [postPromptOpen, setPostPromptOpen] = useState(false);
  const [postPromptUserExists, setPostPromptUserExists] = useState(false);

  useEffect(() => {
    const fetchSite = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sites/${siteId}`);
        if (!response.ok) throw new Error('Site non trouvé');
        setSite(await response.json());
      } catch (error) {
        toast.error('Site non trouvé');
        navigate('/sites');
      } finally {
        setLoading(false);
      }
    };
    fetchSite();
  }, [siteId, navigate]);

  const handleRequestVisite = async () => {
    if (!selectedDate) {
      toast.error('Veuillez sélectionner une date');
      return;
    }
    if (!user) {
      // Validate anon fields
      if (!anonData.nom || !anonData.prenom || !anonData.email || !anonData.telephone) {
        toast.error('Renseignez vos coordonnées complètes');
        return;
      }
    }
    setRequesting(true);
    try {
      let response;
      if (user && token) {
        response = await fetch(`${API_URL}/api/visites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          credentials: 'include',
          body: JSON.stringify({ type_visite: 'SITE', site_id: siteId, date_visite: selectedDate.toISOString() })
        });
      } else {
        response = await fetch(`${API_URL}/api/visites/anonyme`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type_visite: 'SITE',
            site_id: siteId,
            date_visite: selectedDate.toISOString(),
            ...anonData
          })
        });
      }

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur');
      }

      const data = await response.json();
      setDialogOpen(false);

      if (user) {
        toast.success('Demande de visite envoyée');
      } else {
        setPostPromptUserExists(!!data.user_exists);
        setPostPromptOpen(true);
      }
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la demande');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (!site) return null;

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/sites')} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux sites
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Images */}
          <div>
            {site.photos?.length > 0 ? (
              <div className="space-y-4">
                <img src={site.photos[0]} alt={site.nom || site.ville} className="w-full h-80 object-cover rounded-2xl" />
                {site.photos.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {site.photos.slice(1, 5).map((photo, idx) => (
                      <img key={idx} src={photo} alt="" className="w-full h-20 object-cover rounded-lg" />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-80 bg-muted rounded-2xl flex items-center justify-center">
                <Image className="w-16 h-16 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-primary">{site.type_document?.replace('_', ' ') || 'Non spécifié'}</Badge>
              {site.archive && <Badge className="bg-gray-500">Archivé</Badge>}
            </div>
            <h1 className="text-3xl font-bold font-serif mb-4">{site.nom || site.ville}</h1>
            <p className="text-muted-foreground mb-6">{site.description}</p>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-primary" />
                <span>{site.ville}, Sénégal</span>
              </div>
              {site.latitude && site.longitude && (
                <div className="flex items-center gap-3">
                  <Map className="w-5 h-5 text-primary" />
                  <span>GPS: {site.latitude}, {site.longitude}</span>
                </div>
              )}
            </div>

            <div className="flex gap-3 flex-wrap">
              <Link to={`/sites/${siteId}/terrains`} className="flex-1 min-w-[140px]">
                <Button className="w-full btn-pill">Voir les terrains</Button>
              </Link>
              <Button variant="outline" onClick={() => setDialogOpen(true)} data-testid="request-visit-btn">
                <CalendarIcon className="w-4 h-4 mr-2" /> Demander une visite
              </Button>
              {getYouTubeEmbedUrl(site.video_url) && (
                <Button
                  variant="outline"
                  onClick={() => setVideoDialogOpen(true)}
                  data-testid="view-site-video-btn"
                  className="text-primary border-primary/40 hover:bg-primary/10"
                >
                  <PlayCircle className="w-4 h-4 mr-2" /> Visualiser la vidéo
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Visite Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Demander une visite</DialogTitle>
              <DialogDescription>Sélectionnez une date pour visiter le site {site.nom || site.ville}</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date()}
                locale={fr}
                className="rounded-md border mx-auto"
              />
              {!user && (
                <div className="space-y-3 border-t pt-4">
                  <p className="text-sm text-muted-foreground">Vos coordonnées (vous n'êtes pas connecté)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Prénom *</Label><Input value={anonData.prenom} onChange={(e) => setAnonData({...anonData, prenom: e.target.value})} data-testid="anon-prenom-input" /></div>
                    <div><Label className="text-xs">Nom *</Label><Input value={anonData.nom} onChange={(e) => setAnonData({...anonData, nom: e.target.value})} data-testid="anon-nom-input" /></div>
                  </div>
                  <div><Label className="text-xs">Email *</Label><Input type="email" value={anonData.email} onChange={(e) => setAnonData({...anonData, email: e.target.value})} data-testid="anon-email-input" /></div>
                  <div><Label className="text-xs">Téléphone *</Label><Input value={anonData.telephone} onChange={(e) => setAnonData({...anonData, telephone: e.target.value})} data-testid="anon-telephone-input" /></div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button
                onClick={handleRequestVisite}
                disabled={requesting || !selectedDate || (!user && (!anonData.nom || !anonData.prenom || !anonData.email || !anonData.telephone))}
                data-testid="confirm-visite-btn"
              >
                {requesting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <PostAnonAccountPrompt
          open={postPromptOpen}
          onOpenChange={setPostPromptOpen}
          userExists={postPromptUserExists}
          prefillNom={anonData.nom}
          prefillPrenom={anonData.prenom}
          prefillEmail={anonData.email}
          prefillTelephone={anonData.telephone}
        />

        {/* Video Dialog */}
        <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
          <DialogContent className="max-w-4xl p-0 overflow-hidden" data-testid="site-video-dialog">
            <DialogHeader className="p-6 pb-2">
              <DialogTitle>Vidéo du site - {site.nom || site.ville}</DialogTitle>
              <DialogDescription>Découvrez le site en vidéo</DialogDescription>
            </DialogHeader>
            <div className="w-full aspect-video bg-black">
              {videoDialogOpen && getYouTubeEmbedUrl(site.video_url) && (
                <iframe
                  key={site.video_url}
                  src={`${getYouTubeEmbedUrl(site.video_url)}?autoplay=1&rel=0`}
                  title={`Vidéo du site ${site.nom || site.ville}`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  data-testid="site-video-iframe"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
